import {
	AdSlot,
	AdSlotEvent,
	AdSlotStatus,
	BaseServiceSetup,
	communicationService,
	context,
	displayAndVideoAdsSyncContext,
	InstantConfigService,
	JWPlayerManager,
	jwpSetup,
	slotService,
	targetingService,
	utils,
	VastResponseData,
	VastTaglessRequest,
	videoDisplayTakeoverSynchronizer,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
// eslint-disable-next-line no-restricted-imports
import { iasVideoTracker } from '../../../ad-products/video/porvata/plugins/ias/ias-video-tracker';
import { BIDDERS_BIDDING_DONE } from '../../../communication/events/events-bidders';
import {
	VIDEO_EVENT,
	VIDEO_PLAYER_RENDERED,
	VIDEO_SETUP,
} from '../../../communication/events/events-video';
import { getMediaWikiVariable } from '../utils/get-media-wiki-variable';

const logGroup = 'player-setup';
const primaryVideoAdSlotName = 'featured';
let videoAdImpressionEmitted = false;

@Injectable()
export class PlayerSetup extends BaseServiceSetup {
	private readonly connatixFeaturedVideoEnabled: boolean;

	constructor(
		protected instantConfig: InstantConfigService,
		protected globalTimeout: utils.GlobalTimeout,
		protected jwpManager: JWPlayerManager,
		protected vastTaglessRequest: VastTaglessRequest,
	) {
		super(instantConfig, globalTimeout);
		this.jwpManager = jwpManager ? jwpManager : new JWPlayerManager();

		const featuredVideoMapping = getMediaWikiVariable('wgArticleFeaturedVideo')?.mapping;
		const hasConnatixMapping = !!(
			featuredVideoMapping?.connatixMediaId || featuredVideoMapping?.connatixPlaylistId
		);

		this.connatixFeaturedVideoEnabled =
			this.instantConfig.get('icFeaturedVideoPlayer') === 'connatix' && hasConnatixMapping;
	}

	async call() {
		communicationService.on(VIDEO_PLAYER_RENDERED, () => {
			this.loadIasTrackerIfEnabled();
		});
		const showAds = !context.get('options.wad.blocking') && !utils.queryString.get('novideoads');
		const adSlot = this.getPrimaryVideoAdSlot();

		targetingService.set(
			'player',
			this.connatixFeaturedVideoEnabled ? 'cnx' : 'jwp',
			primaryVideoAdSlotName,
		);
		const vastResponse: VastResponseData | undefined =
			showAds &&
			displayAndVideoAdsSyncContext.isSyncEnabled() &&
			displayAndVideoAdsSyncContext.isTaglessRequestEnabled()
				? await this.vastTaglessRequest.getVast()
				: undefined;

		this.connatixFeaturedVideoEnabled
			? PlayerSetup.initConnatixPlayer(showAds, adSlot, vastResponse)
			: await this.initJWPlayer(showAds, vastResponse);
	}

	private async initJWPlayer(showAds: boolean, vastResponse?: VastResponseData) {
		utils.logger(logGroup, 'JWP with ads controlled by AdEngine enabled');

		const strategyRulesEnabled = context.get('options.video.enableStrategyRules');

		if (vastResponse?.xml) {
			displayAndVideoAdsSyncContext.setVastRequestedBeforePlayer();
			utils.logger(logGroup, 'display and video sync response available');
		}

		if (showAds && !strategyRulesEnabled) {
			communicationService.dispatch(
				jwpSetup({
					showAds,
					autoplayDisabled: false,
					vastXml: vastResponse?.xml,
				}),
			);
			this.jwpManager.manage();
		} else if (strategyRulesEnabled) {
			utils.logger(
				logGroup,
				'JWP Strategy Rules enabled - AdEngine does not control ads in JWP anymore',
			);
			communicationService.dispatch(
				jwpSetup({
					showAds,
					autoplayDisabled: false,
					vastUrl: await this.vastTaglessRequest.buildTaglessVideoRequest(false),
					vastXml: vastResponse?.xml,
					strategyRulesEnabled,
				}),
			);
		} else {
			utils.logger(logGroup, 'ad block detected, without ads');
			communicationService.dispatch(
				jwpSetup({
					showAds,
					autoplayDisabled: false,
				}),
			);
		}
	}

	private getPrimaryVideoAdSlot() {
		const adSlot =
			slotService.get(primaryVideoAdSlotName) || new AdSlot({ id: primaryVideoAdSlotName });

		if (this.connatixFeaturedVideoEnabled) {
			adSlot.config.adProduct = 'cnx-featured';
		}

		if (!slotService.get(primaryVideoAdSlotName)) {
			slotService.add(adSlot);
		}

		return adSlot;
	}

	private loadIasTrackerIfEnabled(): void {
		if (context.get('options.video.iasTracking.enabled')) {
			utils.logger(logGroup, 'Loading IAS tracker for video player');
			iasVideoTracker.load();
		}
	}

	private static initConnatixPlayer(
		showAds: boolean,
		adSlot: AdSlot,
		vastResponse?: VastResponseData,
	) {
		utils.logger(logGroup, 'Connatix with ads not controlled by AdEngine enabled');

		if (vastResponse?.xml) {
			displayAndVideoAdsSyncContext.setVastRequestedBeforePlayer();
			utils.logger(logGroup, 'display and video sync response available');
		}

		communicationService.on(
			VIDEO_EVENT,
			(payload) => {
				const { name, state } = payload.videoEvent;

				PlayerSetup.resolveVideoDisplaySyncBasedOnPlayerEvent(name, state, adSlot);
			},
			false,
		);

		communicationService.on(
			BIDDERS_BIDDING_DONE,
			({ slotName }) => {
				if (slotName === primaryVideoAdSlotName) {
					PlayerSetup.emitVideoSetupEvent(showAds, adSlot, vastResponse);
				}
			},
			false,
		);
	}

	public static resolveVideoDisplaySyncBasedOnPlayerEvent(
		eventName: string,
		state: any,
		adSlot: AdSlot,
	): void {
		const wasPlayEmittedBeforeAd = (eventName: string) => {
			return eventName === 'play' && !videoAdImpressionEmitted;
		};

		if (eventName === 'adImpression') {
			videoAdImpressionEmitted = true;
			videoDisplayTakeoverSynchronizer.resolve(
				state.vastParams.lineItemId,
				state.vastParams.creativeId,
			);
			adSlot.setStatus(AdSlotStatus.STATUS_SUCCESS);
			adSlot.emit(AdSlotEvent.VIDEO_AD_IMPRESSION);
		} else if (wasPlayEmittedBeforeAd(eventName)) {
			PlayerSetup.registerTimeoutForVideoDisplayTakeoverSync(
				() => videoAdImpressionEmitted === true,
			);
		} else if (['adError', 'playError'].includes(eventName)) {
			videoDisplayTakeoverSynchronizer.resolve();
		} else {
			console.warn(
				'resolveVideoDisplaySyncBasedOnPlayerEvent() called with not handled event: ',
				eventName,
			);
		}
	}

	private static registerTimeoutForVideoDisplayTakeoverSync(condition: () => boolean): void {
		new utils.WaitFor(condition, 1, displayAndVideoAdsSyncContext.getSyncTimeout())
			.until()
			.then(() => {
				videoDisplayTakeoverSynchronizer.resolve();
			});
	}

	private static emitVideoSetupEvent(
		showAds: boolean,
		adSlot: AdSlot,
		vastResponse?: VastResponseData,
	) {
		communicationService.emit(VIDEO_SETUP, {
			showAds,
			autoplayDisabled: false,
			videoAdUnitPath: adSlot.getVideoAdUnit(),
			targetingParams: utils.getCustomParameters(
				adSlot,
				{
					player: 'cnx',
				},
				false,
			),
			vastXml: vastResponse?.xml,
		});
	}
}
